import type { KeyboardEvent } from "react"

/**
 * `onClose` for a MUI Dialog that closes on the Esc key only.
 *
 * MUI calls `onClose` for both Esc and a click on the backdrop. The dialogs using this hold a
 * form or code that a stray click outside must not throw away, so they used to have no
 * `onClose` at all — and with it no Esc either. Esc now does exactly what the dialog's own
 * Cancel / Close button does.
 *
 * @param handler the Cancel / Close button's handler
 * @param disabled mirrors the button's `disabled` (e.g. while the form is being submitted)
 */
export const closeOnEscape =
    (handler: () => void, disabled: boolean = false) =>
    (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "escapeKeyDown" && !disabled) handler()
    }

/**
 * Elements that already do something with Enter themselves: a multi-line field adds a new
 * line, a focused button activates itself (Enter on Cancel must stay Cancel), a select or an
 * autocomplete picks an option or adds a tag.
 */
const HANDLES_ENTER_ITSELF =
    'textarea, [contenteditable="true"], button, a[href], [role="button"], [role="combobox"], ' +
    '[role="listbox"], [role="option"], [role="menuitem"]'

/**
 * `onKeyDown` for a MUI Dialog that confirms it with Enter — the counterpart of
 * `closeOnEscape`. Set on individual dialogs on purpose, never as a global shortcut, so forms
 * outside dialogs (login, ...) keep the browser's own behaviour.
 *
 * Ctrl/Cmd+Enter confirms even from a multi-line field, the usual way to submit from one.
 *
 * @param handler the dialog's primary button's handler
 * @param disabled mirrors the button's `disabled`
 */
export const confirmOnEnter =
    (handler: () => void, disabled: boolean = false) =>
    (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" || event.repeat || event.nativeEvent.isComposing) return
        if (event.shiftKey || event.altKey) return

        const target = event.target as HTMLElement
        // a dialog opened from inside another one bubbles through the React tree into the
        // outer one as well — only Enter pressed in THIS dialog's own DOM belongs to it
        if (!event.currentTarget.contains(target)) return

        const withModifier = event.ctrlKey || event.metaKey
        if (!withModifier && target.closest(HANDLES_ENTER_ITSELF)) return

        // also stops the browser's implicit form submission, so nothing is submitted twice
        event.preventDefault()
        event.stopPropagation()
        if (!disabled) handler()
    }

/**
 * `confirmOnEnter` for a dialog that is a form: submits it the same way its submit button
 * would, and not at all while that button is disabled.
 *
 * The browser already submits a form on Enter, but only from a single-line input. Routing
 * everything through here makes Enter work wherever the focus is in the dialog, and — since
 * `confirmOnEnter` cancels the native submission — still submits exactly once.
 */
export const submitFormOnEnter = (event: KeyboardEvent<HTMLElement>) => {
    // set on a dialog, the form is inside it; set on a form (a side panel), it is the form
    const form =
        event.currentTarget instanceof HTMLFormElement
            ? event.currentTarget
            : event.currentTarget.querySelector("form")
    const submitButton = form?.querySelector<HTMLButtonElement>('button[type="submit"]')

    confirmOnEnter(
        () => form?.requestSubmit(submitButton ?? undefined),
        !form || Boolean(submitButton?.disabled)
    )(event)
}
